import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../models/NameUtils';
import { getDefaultDateStringValue } from '../../../../../models/UiUtils';
import {
	useCreateLegalMatterMutation,
	useUpdateLegalMatterMutation,
	invalidateAllLegalMatterFilters,
	invalidateAllLegalMatterSummary,
} from '../../../../../queries';
import CalendarIconUrl from '../../../../assets/icon_calendar.svg';
import { CloseButton } from '../../../../components/CloseButton';
import { DayPicker } from '../../../../components/DayPicker';
import { DeprecatedPopover, PopoverType } from '../../../../components/DeprecatedPopover';
import { Modal } from '../../../../components/Modal';
import { Input } from '../../../../components-v2/Input';
import { SimpleAutoCompleteSearchField } from '../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { ClearFieldIcon } from '../../../../components/svgs/icons/ClearFieldIcon';
import { FolderIcon } from '../../../../components/svgs/icons/FolderIcon';
import { white } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from '../../EditModal/styles';
import { MatterStageSearch } from '../MatterStageSearch';
import { MatterStatusSearch } from '../MatterStatusSearch';
import { MatterPracticeAreaSearch } from '../MatterPracticeAreaSearch';

export const EditMatterModal = ({
	initialMatter,
	isOpen,
	setIsOpen,
	onSave,
	onCancel,
}: {
	initialMatter: Partial<Api.ILegalMatter>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onSave: (matter: Api.ILegalMatter) => void;
	onCancel: () => void;
}) => {
	const [contact, setContact] = React.useState<Api.IContact | null>(initialMatter.contact ?? null);
	const [matterName, setMatterName] = React.useState(initialMatter.matterName ?? '');
	const [matterNumber, setMatterNumber] = React.useState(initialMatter.matterNumber ?? '');
	const [stageObj, setStageObj] = React.useState<Api.ILegalMatterStage | null>(initialMatter.stage ?? null);
	const [statusObj, setStatusObj] = React.useState<Api.ILegalMatterStatus | null>(initialMatter.status ?? null);
	const [practiceAreaObj, setPracticeAreaObj] = React.useState<Api.ILegalMatterPracticeArea | null>(
		initialMatter.practiceArea ?? null
	);
	const [openDate, setOpenDate] = React.useState<Date | null>(
		initialMatter.openDate ? moment(initialMatter.openDate).toDate() : null
	);
	const [closeDate, setCloseDate] = React.useState<Date | null>(
		initialMatter.closeDate ? moment(initialMatter.closeDate).toDate() : null
	);
	const [openDatePickerOpen, setOpenDatePickerOpen] = React.useState(false);
	const [closeDatePickerOpen, setCloseDatePickerOpen] = React.useState(false);

	const isEditing = !!initialMatter.id;

	// Reset form fields when initialMatter changes (for editing different matters)
	React.useEffect(() => {
		setContact(initialMatter.contact ?? null);
		setMatterName(initialMatter.matterName ?? '');
		setMatterNumber(initialMatter.matterNumber ?? '');
		setStageObj(initialMatter.stage ?? null);
		setStatusObj(initialMatter.status ?? null);
		setPracticeAreaObj(initialMatter.practiceArea ?? null);
		setOpenDate(initialMatter.openDate ? moment(initialMatter.openDate).toDate() : null);
		setCloseDate(initialMatter.closeDate ? moment(initialMatter.closeDate).toDate() : null);
	}, [initialMatter]);

	const createMatterMutation = useCreateLegalMatterMutation({
		onSuccess: (matter: Api.ILegalMatter) => {
			toast.success('Matter successfully created');
			invalidateAllLegalMatterFilters();
			invalidateAllLegalMatterSummary();
			onSave(matter);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to create matter');
		},
	});

	const updateMatterMutation = useUpdateLegalMatterMutation({
		onSuccess: (matter: Api.ILegalMatter) => {
			toast.success('Matter successfully updated');
			invalidateAllLegalMatterFilters();
			invalidateAllLegalMatterSummary();
			onSave(matter);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to update matter');
		},
	});

	const save = () => {
		const updatedMatter: Partial<Api.ILegalMatter> = {
			...initialMatter,
			contactId: contact?.id ?? '',
			matterName,
			matterNumber,
			stageId: stageObj?.id,
			statusId: statusObj?.id,
			practiceAreaId: practiceAreaObj?.id,
			openDate: openDate ? moment(openDate).format('YYYY-MM-DD') : '',
			closeDate: closeDate ? moment(closeDate).format('YYYY-MM-DD') : undefined,
		};

		if (isEditing) {
			updateMatterMutation.mutateAsync({ matter: updatedMatter as Api.ILegalMatter });
			return;
		}
		createMatterMutation.mutateAsync({ matter: updatedMatter });
	};

	const cancel = () => {
		onCancel();
		setIsOpen(false);
	};

	const onContactCleared = () => {
		setContact(null);
	};

	const onContactSelected = ({ selection }: { selection: Api.IEntity | string | Api.IUser }) => {
		if (typeof selection !== 'string' && 'id' in selection) {
			setContact(selection as Api.IContact);
		}
	};

	const onOpenDateSelected = (value: Date) => {
		setOpenDate(value);
		setOpenDatePickerOpen(false);
	};

	const onCloseDateSelected = (value: Date) => {
		setCloseDate(value);
		setCloseDatePickerOpen(false);
	};

	const toggleOpenDatePickerOpen = () => {
		setOpenDatePickerOpen(!openDatePickerOpen);
	};

	const toggleCloseDatePickerOpen = () => {
		setCloseDatePickerOpen(!closeDatePickerOpen);
	};

	const clearOpenDate = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		setOpenDate(null);
	};

	const clearCloseDate = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		setCloseDate(null);
	};

	const canSave =
		!!contact && !!matterName && !!matterNumber && !!stageObj && !!statusObj && !!practiceAreaObj && !!openDate;

	const onClose = () => {
		setIsOpen(false);
	};

	return (
		<Modal
			className={css(styleSheet.modalContainer)}
			isOpen={isOpen}
			useDefaultHeader={true}
			onRequestClose={cancel}
			headerAccessory={
				<div className={css(styleSheet.modalHeader)}>
					<FolderIcon fillColor={white} />
					<CloseButton onClick={onClose} fillColor={white} />
				</div>
			}
		>
			<article className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>{`${isEditing ? 'Edit' : 'Add New'} Matter`}</h3>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Contact *</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<SimpleAutoCompleteSearchField
							onClear={onContactCleared}
							onItemSelected={onContactSelected}
							resultsLimit={5}
							pageSize={5}
							placeholder='Search for contact...'
							style={styleSheet.contactSearchField}
							type={Api.ResourceAutoCompleteViewModelType.Contact}
							initialSearchQuery={initialMatter.contact ? getDisplayName(initialMatter.contact) : ''}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Matter Name *</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<Input
							id='matter-modal-edit-name'
							onChange={e => setMatterName(e.target.value)}
							type='text'
							value={matterName}
							placeholder='Enter matter name'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Matter Number *</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<Input
							disabled={isEditing}
							id='matter-modal-edit-number'
							onChange={e => setMatterNumber(e.target.value)}
							type='text'
							value={matterNumber}
							placeholder='Enter matter number'
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Stage *</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<MatterStageSearch
							onSelectStage={setStageObj}
							onCreateStage={setStageObj}
							initialSearchFragment={stageObj?.name ?? ''}
							onClear={() => setStageObj(null)}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Status *</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<MatterStatusSearch
							onSelectStatus={setStatusObj}
							onCreateStatus={setStatusObj}
							initialSearchFragment={statusObj?.name ?? ''}
							onClear={() => setStatusObj(null)}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Practice Area *</label>

					<div className={css(styleSheet.formControlInputWrap)}>
						<MatterPracticeAreaSearch
							onSelectPracticeArea={setPracticeAreaObj}
							onCreatePracticeArea={setPracticeAreaObj}
							initialSearchFragment={practiceAreaObj?.name ?? ''}
							onClear={() => setPracticeAreaObj(null)}
						/>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Open Date *</label>

					<div
						className={css(styleSheet.formControlInputWrap, styleSheet.clickable)}
						onClick={toggleOpenDatePickerOpen}
					>
						<div className={css(styleSheet.dateContainer)}>
							<div className={css(styleSheet.dateFieldButton)}>
								<DeprecatedPopover
									anchor={<img src={CalendarIconUrl} alt='Calendar' />}
									dismissOnClickOutside={true}
									isOpen={openDatePickerOpen}
									onRequestClose={toggleOpenDatePickerOpen}
									preferredPlacement='above'
									type={PopoverType.white}
								>
									<DayPicker allowPastDates={true} onDayClick={onOpenDateSelected} selectedDays={openDate} />
								</DeprecatedPopover>
								{!!openDate && <span>{getDefaultDateStringValue(openDate)}</span>}
							</div>
							<button onClick={(e: React.MouseEvent<HTMLElement>) => clearOpenDate(e)}>
								<ClearFieldIcon />
							</button>
						</div>
					</div>
				</div>

				<div className={css(styleSheet.formControl)}>
					<label className={css(styleSheet.label)}>Close Date</label>

					<div
						className={css(styleSheet.formControlInputWrap, styleSheet.clickable)}
						onClick={toggleCloseDatePickerOpen}
					>
						<div className={css(styleSheet.dateContainer)}>
							<div className={css(styleSheet.dateFieldButton)}>
								<DeprecatedPopover
									anchor={<img src={CalendarIconUrl} alt='Calendar' />}
									dismissOnClickOutside={true}
									isOpen={closeDatePickerOpen}
									onRequestClose={toggleCloseDatePickerOpen}
									preferredPlacement='above'
									type={PopoverType.white}
								>
									<DayPicker allowPastDates={true} onDayClick={onCloseDateSelected} selectedDays={closeDate} />
								</DeprecatedPopover>
								{!!closeDate && <span>{getDefaultDateStringValue(closeDate)}</span>}
							</div>
							<button onClick={(e: React.MouseEvent<HTMLElement>) => clearCloseDate(e)}>
								<ClearFieldIcon />
							</button>
						</div>
					</div>
				</div>

				<footer className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={save} disabled={!canSave}>
						Save
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={cancel}>
						Cancel
					</button>
				</footer>
			</article>
		</Modal>
	);
};
